import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { adminLogin, adminMe, changeAdminPassword } from "../lib/api";
import Seo from "../components/Seo";

const MIN_PASSWORD_LENGTH = 8;

const AdminSecurity = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [checkingSession, setCheckingSession] = useState(Boolean(token));
  const [username, setUsername] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    if (!token) {
      setCheckingSession(false);
      setUsername("");
      return;
    }
    setCheckingSession(true);
    adminMe(token)
      .then((data) => {
        setUsername(data?.username || "admin");
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        setToken("");
        setUsername("");
      })
      .finally(() => {
        setCheckingSession(false);
      });
  }, [token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    try {
      const data = await adminLogin(loginForm.username, loginForm.password);
      localStorage.setItem("admin_token", data.access_token);
      setToken(data.access_token);
      setLoginForm({ username: "", password: "" });
      toast({ title: "Sesion iniciada" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: "No se pudo iniciar sesion",
        description: typeof detail === "string" ? detail : "Verifica usuario y contrasena.",
        variant: "destructive",
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setUsername("");
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    const currentPassword = passwordForm.currentPassword.trim();
    const newPassword = passwordForm.newPassword.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "Campos incompletos",
        description: "Debes completar todos los campos.",
        variant: "destructive",
      });
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast({
        title: "Contrasena muy corta",
        description: `La nueva contrasena debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: "Confirmacion invalida",
        description: "La confirmacion no coincide con la nueva contrasena.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await changeAdminPassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token,
      );
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      toast({
        title: "Contrasena actualizada",
        description: "Tu nueva clave de administrador ya quedo guardada.",
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 401) {
        handleLogout();
      }
      toast({
        title: "No se pudo actualizar la contrasena",
        description: typeof detail === "string" ? detail : "Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <Seo title="Seguridad Admin" robots="noindex, nofollow" />
        <div className="container mx-auto px-4 max-w-md">
          <Card>
            <CardHeader>
              <CardTitle>Seguridad de administrador</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">Validando sesion...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <Seo title="Seguridad Admin" robots="noindex, nofollow" />
        <div className="container mx-auto px-4 max-w-md">
          <Card>
            <CardHeader>
              <CardTitle>Seguridad de administrador</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="security-admin-username">Usuario</Label>
                  <Input
                    id="security-admin-username"
                    value={loginForm.username}
                    onChange={(event) =>
                      setLoginForm((prev) => ({ ...prev, username: event.target.value }))
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="security-admin-password">Contrasena</Label>
                  <Input
                    id="security-admin-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(event) =>
                      setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                    }
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? "Ingresando..." : "Iniciar sesion"}
                </Button>
              </form>
              <div className="mt-4">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/admin">Ir al panel admin</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <Seo title="Seguridad Admin" robots="noindex, nofollow" />
      <div className="container mx-auto px-4 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Cambiar clave de administrador</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">Sesion activa: {username || "admin"}</p>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="security-current-password">Contrasena actual</Label>
                <Input
                  id="security-current-password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="security-new-password">Nueva contrasena</Label>
                <Input
                  id="security-new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
                  }
                  required
                />
                <p className="text-xs text-gray-500">
                  Minimo {MIN_PASSWORD_LENGTH} caracteres.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="security-confirm-password">Confirmar nueva contrasena</Label>
                <Input
                  id="security-confirm-password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                  }
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? "Guardando..." : "Guardar nueva clave"}
              </Button>
            </form>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button asChild variant="outline">
                <Link to="/admin">Volver al admin</Link>
              </Button>
              <Button type="button" variant="outline" onClick={handleLogout}>
                Cerrar sesion
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSecurity;
